import { useCallback, useEffect, useState } from 'react';

export interface UseCommandPaletteResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * #659 §UAT-2026-07-08-R659 §R659-3 — 전역 커맨드 팔레트 오픈 상태 + `Ctrl+K`/`Cmd+K` 단축키.
 *
 * `packages/ui-kit/src/AppShell/AppShell.tsx` 의 `[` 사이드바 토글 단축키 가드 패턴
 * (`INPUT`/`TEXTAREA`/`SELECT` 포커스 시 무시)을 그대로 차용해, 폼 입력 도중 Ctrl+K 를
 * 오검지하지 않도록 한다. Esc 닫힘은 `FormDialog` 패턴처럼 오픈 상태에서만 별도
 * 리스너로 처리한다(팔레트 자체의 검색 인풋이 포커스된 상태에서도 항상 닫혀야 하므로
 * 위의 폼 필드 가드와는 독립적으로 동작).
 *
 * 마운트 위치: `AuthLayout.tsx` (AppShell 을 감싸는 인증 셸) — 전역 1회.
 */
export function useCommandPalette(): UseCommandPaletteResult {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Ctrl+K / Cmd+K — INPUT/TEXTAREA/SELECT 포커스 시 무시(AppShell `[` 가드 차용)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const isToggleCombo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isToggleCombo) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggle]);

  // Esc — 오픈 상태에서만 닫힘 (FormDialog ESC 패턴 차용, R7-049)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  return { isOpen, open, close, toggle };
}
